<template>
    <div class="position-relative">
        <div v-if="loading" class="loading-overlay">
            <b-spinner label="Загрузка..."></b-spinner>
        </div>        

        <div class="d-flex align-items-center justify-content-space-between mb-3">
            <div><h5 class="mb-0">Шаблоны счетов компании</h5></div>
            <div>
                <b-button @click="updateTemplateActive()" variant="secondary">Услуги компании</b-button>
                <b-button @click="newTemplate" variant="success" class="ml-1">Добавить шаблон</b-button>
            </div>
        </div>    

        <b-table 
            :items="templates" 
            :fields="fields" 
            bordered 
            responsive 
            show-empty
            :caption-top="false"
        >
            <template #empty="scope">
                <div>Не создано ни одного шаблона счетов для этой компании. Создайте, нажав «Добавить шаблон».</div>
            </template>

            <template #cell(services)="row">
                <div 
                    v-for="(service, index) in row.item.services" 
                    :key="service.id"
                >
                    <div>{{ service.name }}
                        <span class="text-muted small" v-if="service.columns && formatServiceColumns(service.columns).length > 0">
                            (<span 
                                v-for="(column, colIndex) in formatServiceColumns(service.columns)"
                                :key="colIndex"
                            >{{ column.name }} -> {{ column.default }}{{ colIndex < formatServiceColumns(service.columns).length - 1 ? '; ' : '' }}</span>)
                        </span>
                    </div>
                </div>
            </template>            

            <template #cell(actions)="row">
                <div class="text-center" style="width: 90px;">
                    <b-button 
                        @click="editTemplate(row.item)" 
                        variant="table"
                        size="sm"
                    >
                        <i class="fas fa-pencil-alt"></i>
                    </b-button>
                    <b-button 
                        @click="deleteTemplate(row.item.id)" 
                        variant="danger"
                        size="sm"
                        class="ml-1"
                    >
                        <i class="fas fa-trash"></i>
                    </b-button>  
                </div>
            </template>
        </b-table>

        <b-modal 
            v-model="showModal" 
            :title="modalTitle" 
            size="lg" 
            centered
        >
            <b-form @submit.prevent="saveTemplate">
                <b-form-group label="Название шаблона" label-for="template-name">
                    <b-form-input
                        id="template-name"
                        v-model="form.name"
                        required
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Добавить услуги">
                    <b-form-select
                        v-model="form.services"
                        :options="formattedServiceOptions"
                        :select-size="10"
                        style="height: 400px !important;" 
                        multiple
                    ></b-form-select>
                </b-form-group>
            </b-form>

            <template #modal-footer>
                <b-button 
                    variant="secondary" 
                    @click="showModal = false"
                >Закрыть</b-button>
                <b-button 
                    variant="primary" 
                    @click="saveTemplate"
                >Сохранить</b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { API_COMPANY_FINANCE_TEMPLATES, API_COMPANY_FINANCE_SERVICES } from '@/constants';

export default {
    name: "CompanyFinanceInvoiceTemplateConstructor",
    props: {
        companyId: {
            type: Number | String,
            required: true
        }
    },    
    data() {
      return {
        loading: false,
        templates: [],
        fields: [
            { key: "name", label: "Название шаблона" },
            { key: "services", label: "Услуги" },
            { key: "actions", label: "Действия", class: ['w110px', 'text-center'] }
        ],
        showModal: false,
        form: {
            id: null,
            name: "",
            services: []
        },
        serviceOptions: [],
        formattedServiceOptions: []
      };
    },
    computed: {
        modalTitle() {
            return this.form.id ? "Редактирование шаблона" : "Создание шаблона";
        }
    },
    mounted() {
        this.fetchTemplates();
        this.fetchServices();
    },
    methods: {
        fetchTemplates() {
            this.loading = true;
            api.call("get", `${API_COMPANY_FINANCE_TEMPLATES}?company_id=${this.companyId}`)
                .then(response => {
                    this.templates = response.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchServices() {
            api.call("get", `${API_COMPANY_FINANCE_SERVICES}?company_id=${this.companyId}`)
                .then(response => {
                    this.serviceOptions = response.data;
                    this.formattedServiceOptions = response.data.map(service => ({
                        value: service.id,
                        text: `${service.name} | ${this.formatServiceColumns(service.columns)
                            .map(col => `${col.name}: ${col.default}`)
                            .join('; ')}`
                    }));
                });
        },
        newTemplate() {
            this.form = {
                id: null,
                name: "",
                services: []
            };
            this.showModal = true;
        },
        editTemplate(template) {
            let tempTemplate = JSON.parse(JSON.stringify(template));

            this.form = {
                ...tempTemplate,
                services: tempTemplate.services.map(service => service.id)
            };

            this.showModal = true;
        },
        saveTemplate() {
            const method = this.form.id ? "put" : "post";
            const url = this.form.id
                ? `${API_COMPANY_FINANCE_TEMPLATES}/${this.form.id}`
                : `${API_COMPANY_FINANCE_TEMPLATES}`;

            this.loading = true;

            api.call(method, url, {
                ...this.form,
                company_id: this.companyId
            })
                .then(() => {
                    this.showModal = false;
                    this.fetchTemplates();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        deleteTemplate(templateId) {
            this.loading = true;

            api.call("delete", `${API_COMPANY_FINANCE_TEMPLATES}/${templateId}`)
                .then(() => {
                    this.fetchTemplates();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatServiceColumns(columns) {
            return Object.values(columns)
                .filter(column => !['id', 'name', 'sum'].includes(column.key)) // Exclude specific keys
                .slice(0, 3) // Take only the first three columns
                .map(column => ({
                    name: column.name,
                    default: column.default || "—",
                }));
        },
        updateTemplateActive() {
            this.$emit('updateTemplateActive', false);
        }
    }
};
</script>

<style scoped>
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}
</style>
